/**
 * The Share surface (SH-5).
 *
 * `ui/share-modal.js` is a façade over this; the export names, options and
 * resolution shape are unchanged, and `showInviteAcceptModal` stays there
 * because it is a separate landing surface that the merge does not touch.
 *
 * Moved into its own directory so the whole rebuild reverts as one deletion —
 * and so the two source gates that count patterns in `share-modal.js` had to be
 * re-keyed at `ui/share/**` rather than silently passing against a five-line
 * façade, which is what they would have done.
 *
 * This module imports nothing from `shell.js` and nothing from `editing/`. That
 * is not incidental: it is what keeps the dialog importable under jsdom, and it
 * is now asserted against the transitive module graph rather than against one
 * literal string.
 */

import {
  createModal,
  createButton,
  createConfirmDialog,
  copyTextToClipboard
} from '../components.js';
import {
  listProjectMembers,
  addProjectMember,
  removeProjectMember,
  listProjectInvites,
  revokeProjectInvite,
  listReviewLinks,
  listProjectComments,
  createReviewLink,
  revokeReviewLink,
  extendReviewLink
} from '../../templates/auth-api.js';
import { TemplateApiError } from '../../templates/template-api.js';
import { openPopoverMenu, closeActivePopoverMenu } from '../popover-menu.js';
import { icon } from '../icons.js';
import { createRecipientField, isValidRecipient } from './recipient-field.js';
import { illustration } from '../illustrations.js';
import { showToast } from '../toast.js';
import { renderSkeletonRows, createDelayedLoader } from '../feedback.js';
import { reportError } from '../../core/report-error.js';
import { eventBus } from '../../core/events.js';
import {
  mergeAccessRows,
  seatsRemaining,
  countCommentsByLink,
  deriveCapabilities,
  describeDelivery,
  describeLinkState,
  actionsFor
} from './access-model.js';

/** Friendly copy for the server's membership error codes. */
export function memberErrorMessage(err, fallback) {
  if (err instanceof TemplateApiError) {
    const code = err.data?.code;
    if (code === 'user_not_found') {
      return 'No account with that email yet.';
    }
    if (code === 'already_owner') return 'That user already owns this project.';
    if (code === 'member_limit') return err.message;
    if (code === 'owner_only') return 'Only the project owner can manage members.';
    if (err.status === 404) {
      return "This project isn't in the cloud yet — use Save to cloud first, then share it.";
    }
    return err.message || fallback;
  }
  return fallback;
}

function initialsOf(text) {
  const source = String(text || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase() || '?';
}

export function showShareSurface({
  projectId,
  projectName = '',
  currentUser = null,
  // FN-8: 'edit' | 'review' — an EXPLICIT choice from the Share menu, applied
  // to this opening only. Absent means the plain Share entry, which honours the
  // remembered preference. Deliberately not routed through `rememberAccess`:
  // asking for a review link once must not silently redefine the default.
  access: requestedAccess = null
} = {}) {
  if (!projectId) return null;
  const existing = document.querySelector('.pb-share-overlay');
  if (existing) {
    window.setTimeout(() => {
      existing
        .querySelector('[data-testid="share-email-input"], [data-testid="share-reviewer-input"]')
        ?.focus();
    }, 0);
    return null;
  }
  const selfSub = currentUser?.sub ? String(currentUser.sub) : '';

  const { overlay, body, footer, close } = createModal({
    title: 'Share',
    size: 'md',
    overlayClassName: 'pb-share-overlay',
    className: 'pb-share-modal',
    testid: 'share-modal',
    // The general-access statement lives in the footer, pinned under the list:
    // "only the people listed above" is a claim about the WHOLE list, so it
    // belongs after it and must not scroll away with it.
    withFooter: true,
    // PP-0: a dialog states its target. The caller always knows the project;
    // not saying so was the picker-patterns rule going unapplied here.
    context: projectName ? `Private · ${projectName}` : 'Private project',
    // Open-time focus goes through the kit, never a bare .focus() — a
    // `.focus()` in this function would trip `no-hand-rolled-open-focus`, and
    // more to the point it races the focus trap.
    initialFocus: () =>
      overlay.querySelector('[data-testid="share-email-input"]') ||
      overlay.querySelector('[data-testid="share-reviewer-input"]'),
    // The sheet's downward fling dispatches the SAME `trap-escape` event Escape
    // does, deliberately, so one veto covers both gestures. Everything else has
    // to be intercepted below.
    onEscape: () => (tokenIsUnread() ? (guardExit(), false) : undefined)
  });

  // ── the one-shot token's dismissal guard ───────────────────────────────────
  //
  // GUARDED, never blocked. The prototype gated the exit on having copied; that
  // is cut, because `copyTextToClipboard` legitimately returns false on an
  // insecure origin or a denied permission and a copy-gated exit is then an
  // INESCAPABLE dialog. It also protects nothing — the link is live server-side
  // the instant create returns 201, so dismissing loses the token, not the grant.
  //
  // The backdrop and the header × both call `close()` directly and have no veto
  // hook (`components.js`), so they are intercepted in the CAPTURE phase before
  // the kit's own bubble-phase listeners — the same shape `createConfirmDialog`
  // uses to hold itself open over in-flight work.
  let guarding = false;
  const tokenIsUnread = () => Boolean(reveal?.items?.some((item) => !item.copied));
  const unreadLabels = () => (reveal?.items || []).filter((item) => !item.copied);

  async function guardExit() {
    if (guarding) return;
    guarding = true;
    try {
      const unread = unreadLabels();
      const who =
        unread.length === 1
          ? `${unread[0].label}'s link has`
          : unread.length > 1
            ? `${unread.length} links have`
            : 'This reviewer’s link has';
      const leave = await createConfirmDialog({
        title: 'Close without copying the link?',
        message: `${who} not been copied, and ${unread.length > 1 ? 'they cannot' : 'it cannot'} be shown again. Closing now leaves a link nobody has.`,
        confirmLabel: 'Close anyway',
        cancelLabel: 'Keep it open',
        danger: true,
        errorScope: 'ShareModal reveal guard'
      });
      if (leave) {
        reveal = null;
        close();
      }
    } finally {
      guarding = false;
    }
  }

  overlay.addEventListener(
    'click',
    (event) => {
      if (!tokenIsUnread()) return;
      const isBackdrop = event.target === overlay;
      const isCloseBtn =
        typeof window.Element === 'function' &&
        event.target instanceof window.Element &&
        event.target.closest('.pb-modal-close');
      if (!isBackdrop && !isCloseBtn) return;
      event.stopPropagation();
      event.preventDefault();
      void guardExit();
    },
    true
  );

  // ── the body, in the order the eye reads it ────────────────────────────────
  //
  // COMPOSE FIRST, LIST UNDER IT. The dialog answers "who can get to this
  // project?" and the thing you came to do is add someone; the list is the
  // context for that decision, and it grows DOWNWARD from the field that adds
  // to it. The previous order — a description, the list, then the form — put
  // the only control below a list whose length nobody controls.
  const actions = document.createElement('div');
  actions.className = 'pb-share-actions';
  body.appendChild(actions);

  const errorEl = document.createElement('p');
  errorEl.className = 'pb-share-error';
  errorEl.setAttribute('aria-live', 'polite');
  errorEl.hidden = true;
  body.appendChild(errorEl);

  const listHead = document.createElement('div');
  listHead.className = 'pb-share-section-head';
  const listHeadText = document.createElement('span');
  listHeadText.textContent = 'Who has access';
  const listCount = document.createElement('span');
  listCount.className = 'pb-share-section-count';
  listHead.append(listHeadText, listCount);
  body.appendChild(listHead);

  const list = document.createElement('div');
  list.className = 'pb-share-list';
  list.setAttribute('role', 'list');
  list.setAttribute('aria-label', 'Who has access to this project');
  body.appendChild(list);

  // MK-7's lesson: an accessibility affordance a surface creates CONDITIONALLY
  // is created for the first consumer's reason only. `createModal` provides no
  // live region at all, and the old dialog announced errors and nothing else —
  // not an add, not a removal, not the loaded count.
  /**
   * THE GENERAL-ACCESS STATEMENT — the honest default, said out loud.
   *
   * Every comparable surface has a "General access" row because the first
   * question anyone asks of a share dialog is "wait, is this public?". This one
   * had no answer at all, so the reader had to infer it from the absence of a
   * link control. It is a statement rather than a control because there is
   * genuinely nothing to change: there is no public link and no way to make
   * one.
   */
  function buildGeneralAccess() {
    const wrap = document.createElement('div');
    wrap.className = 'pb-share-general';

    const glyph = document.createElement('span');
    glyph.className = 'pb-share-general-icon';
    glyph.setAttribute('aria-hidden', 'true');
    // Trusted static registry markup — never routed through escapeHTML.
    glyph.innerHTML = icon('links', { size: 'sm' }) || '';

    const text = document.createElement('div');
    text.className = 'pb-share-general-text';
    const strong = document.createElement('b');
    strong.textContent = 'Only the people listed above';
    text.append(strong, document.createTextNode(' can open this project.'));
    const sub = document.createElement('span');
    sub.textContent =
      'There is no public link. Review links are per person, expire, and can be revoked.';
    text.appendChild(sub);

    wrap.append(glyph, text);

    // Offered only where it can act. `capabilities.canComment` is false when
    // the deployment has review comments switched off, and a button that mints
    // a link the server refuses is worse than no button.
    if (capabilities.canComment && selfRole === 'owner') {
      wrap.appendChild(
        createButton({
          variant: 'secondary',
          label: 'Get a review link',
          testid: 'share-general-review-link',
          onClick: () => switchToReviewLink('')
        })
      );
    }
    return wrap;
  }

  function renderFooter() {
    if (!footer) return;
    footer.textContent = '';
    footer.appendChild(buildGeneralAccess());
  }

  const live = document.createElement('div');
  live.className = 'pb-sr-only pb-share-live';
  live.setAttribute('role', 'status');
  live.setAttribute('aria-live', 'polite');
  body.appendChild(live);
  const announce = (message) => {
    live.textContent = '';
    window.setTimeout(() => {
      if (live.isConnected) live.textContent = message;
    }, 30);
  };

  let members = [];
  let invites = [];
  let links = [];
  let linksError = null;
  let selfRole = null;
  // SH-6. Remembered per browser: an agency that only ever sends review links
  // should not re-pick "Can comment" on every project.
  // An explicit request wins over the remembered mode for this opening. It is
  // assigned rather than remembered, so closing and reopening returns to the
  // user's own default.
  let access =
    requestedAccess === 'review'
      ? 'comment'
      : requestedAccess === 'edit'
        ? 'edit'
        : readRememberedAccess();
  /** Said once per opening — `paintCompose` runs on every keystroke. */
  let announcedReviewFallback = false;
  // The one-shot token, while it is on screen and unread. Never persisted,
  // never re-rendered after dismissal — there is nothing to re-render from.
  let reveal = null;
  let foldOpen = false;
  let capabilities = deriveCapabilities({});
  let capabilitiesField = null;
  let lastAddError = null;
  // HR's rule: never place something durable inside a container a render
  // rebuilds. `renderRows` replaces the whole list on every mutation, so the
  // row a keyboard user was on has to be named BEFORE the rebuild and re-seated
  // after it — otherwise the popover's focus restore targets a detached node
  // and focus falls to <body> on every single action.
  let focusRowId = null;

  function readRememberedAccess() {
    try {
      const remembered = localStorage.getItem('pb-share-access');
      if (remembered === 'comment' || remembered === 'contentEdit') return remembered;
      return 'edit';
    } catch {
      // Expected flow: private mode, or storage disabled. The default is fine.
      return 'edit';
    }
  }

  function rememberAccess(value) {
    access = value;
    try {
      localStorage.setItem('pb-share-access', value);
    } catch {
      // Expected flow: as above. Losing a preference is not a failure worth a toast.
    }
  }

  function setError(message) {
    errorEl.textContent = message || '';
    errorEl.hidden = !message;
  }

  /**
   * The dead end becomes the discovery moment. "No account with that email yet"
   * is the instant someone is most receptive to learning that review links
   * exist, and it used to be the instant the product told them to go away — so
   * the address they typed is carried straight across into the other mode
   * rather than being retyped.
   */
  function switchToReviewLink(value = '') {
    if (!capabilities.canComment) return false;
    reveal = null;
    setError('');
    rememberAccess('comment');
    renderActions();
    if (value) recipients.keep([value]);
    paintCompose();
    recipients.input.focus();
    announce('Review link option selected');
    return true;
  }

  function renderReviewLinkFork(email) {
    if (!capabilities.canComment) return false;
    const fork = document.createElement('div');
    fork.className = 'pb-share-error-action';

    const message = document.createElement('span');
    message.textContent = 'No account with that email yet.';
    fork.appendChild(message);

    const button = createButton({
      variant: 'ghost',
      size: 'sm',
      label: 'Send them a review link instead',
      onClick: () => switchToReviewLink(email)
    });
    fork.appendChild(button);
    actions.appendChild(fork);
    announce('No account with that email yet. Send them a review link instead.');
    return true;
  }

  function renderStatus(className, text) {
    list.innerHTML = '';
    const el = document.createElement('div');
    el.className = className;
    el.textContent = text;
    list.appendChild(el);
  }

  function runRowAction(scope, work) {
    // The popover calls its handler synchronously and try/catches only a
    // SYNCHRONOUS throw, so a rejected promise here would float.
    work().catch((err) => reportError(scope, err, { user: 'toast' }));
  }

  function buildRowMenu({ label, ariaLabel, items, rowId }) {
    const trigger = createButton({
      variant: 'bare',
      className: 'pb-share-row-trail',
      label,
      ariaLabel: ariaLabel || label,
      ariaHaspopup: 'menu'
    });
    // The caret FOLLOWS the word, so the trigger reads as "Can edit ▾" — a
    // statement of the current access that happens to be openable, rather than
    // a bare glyph you have to click to learn anything from. Appended rather
    // than passed as `icon`, which the kit renders first.
    const trailCaret = document.createElement('span');
    trailCaret.className = 'pb-share-row-caret';
    trailCaret.setAttribute('aria-hidden', 'true');
    // Trusted static registry markup — never routed through escapeHTML.
    trailCaret.innerHTML = icon('chevronDown', { size: 'sm' }) || '';
    trigger.appendChild(trailCaret);
    trigger.dataset.rowId = rowId;
    trigger.addEventListener('click', () => {
      openPopoverMenu(trigger, {
        items,
        owner: 'share-modal',
        align: 'right',
        width: 220,
        menuLabel: label,
        phoneSheet: 'auto'
      });
    });
    return trigger;
  }

  /**
   * Menu items for a row, driven by the model's permission matrix.
   *
   * Every action closes the popover FIRST. `--pb-z-popover-portal` (460) sits
   * above `--pb-z-modal` (455) and `syncModalStack` gives a stacked confirm
   * 456 — so a confirm opened from an open menu renders BEHIND it.
   */
  function itemsFor(row) {
    const allowed = actionsFor(row, { selfRole });
    const items = [];
    if (allowed.includes('remove')) {
      items.push({
        label: 'Remove access',
        danger: true,
        onSelect: () => {
          closeActivePopoverMenu();
          focusRowId = row.id;
          runRowAction('ShareModal remove', () => removeMember(row));
        }
      });
    }
    if (allowed.includes('extend')) {
      items.push({
        label: 'Extend to 90 days',
        onSelect: () => {
          closeActivePopoverMenu();
          focusRowId = row.id;
          runRowAction('ShareModal extend link', () => extendLink(row));
        }
      });
    }
    if (allowed.includes('revoke-link')) {
      items.push({
        // Shown only once, at creation. Present and DISABLED rather than absent
        // because "where is copy?" is a predictable search that deserves an
        // answer — and the label is the answer, so it needs no sub-line.
        label: 'Link shown only once',
        disabled: true
      });
      items.push({ type: 'separator' });
      items.push({
        label: 'Revoke link',
        danger: true,
        onSelect: () => {
          closeActivePopoverMenu();
          focusRowId = row.id;
          runRowAction('ShareModal revoke link', () => revokeLink(row));
        }
      });
    }
    if (allowed.includes('recreate')) {
      items.push({
        label: 'Create a new link for them',
        onSelect: () => {
          closeActivePopoverMenu();
          switchToReviewLink(row.name);
        }
      });
    }
    if (allowed.includes('revoke-invite')) {
      if (capabilities.canComment) {
        items.push({
          label: 'Send a review link instead',
          onSelect: () => {
            closeActivePopoverMenu();
            switchToReviewLink(row.email);
          }
        });
        items.push({ type: 'separator' });
      }
      items.push({
        label: 'Revoke invitation',
        danger: true,
        onSelect: () => {
          closeActivePopoverMenu();
          focusRowId = row.id;
          runRowAction('ShareModal revoke invite', () => revokeInvite(row));
        }
      });
    }
    return items;
  }

  /**
   * ONE ROW SHAPE FOR FOUR KINDS OF ACCESS.
   *
   * Owner, editor, pending invitation and live review link are four answers to
   * one question, so they are four rows in one list rather than two lists in
   * two dialogs. What separates them is the AVATAR (a link is not a person and
   * must never wear a person's initials disc), a state tag beside the name, and
   * a facts line whose vocabulary comes from the model — three copies of
   * "Invitation failed" is how two of them end up disagreeing on one screen.
   */
  function buildRow(row) {
    const el = document.createElement('div');
    el.className = 'pb-share-member';
    if (row.kind === 'invite') el.classList.add('is-pending');
    if (!row.live) el.classList.add('is-inactive');
    el.setAttribute('role', 'listitem');
    el.dataset.rowId = row.id;
    el.dataset.rowKind = row.kind;
    if (row.kind !== 'invite') el.dataset.memberSub = row.sub || '';

    const avatar = document.createElement('span');
    avatar.className = 'pb-share-avatar';
    avatar.setAttribute('aria-hidden', 'true');
    if (row.kind === 'link') {
      // A link wearing a person's avatar is the first thing that makes a merged
      // list unreadable.
      avatar.classList.add('is-link');
      avatar.innerHTML = icon('links', { size: 'sm' }) || '';
    } else if (row.kind === 'invite') {
      // Nor is an address that has not been accepted yet a person — it is an
      // envelope in flight.
      avatar.classList.add('is-invite');
      avatar.innerHTML = icon('share', { size: 'sm' }) || '';
    } else {
      avatar.textContent = initialsOf(row.name);
    }

    const main = document.createElement('div');
    main.className = 'pb-share-member-main';

    const nameEl = document.createElement('div');
    nameEl.className = 'pb-share-member-name';
    const nameText = document.createElement('span');
    nameText.className = 'pb-share-member-trunc';
    nameText.textContent =
      (row.kind === 'invite' ? row.email || row.name : row.name) + (row.isSelf ? ' (you)' : '');
    nameEl.appendChild(nameText);

    // The state tag sits WITH the name, because it qualifies the name — an
    // address that has not accepted yet, a link with days left on it.
    const tag = rowTag(row);
    if (tag) {
      const tagEl = document.createElement('span');
      tagEl.className = `pb-share-tag ${tag.tone}`;
      tagEl.textContent = tag.text;
      nameEl.appendChild(tagEl);
    }
    main.appendChild(nameEl);

    const secondary =
      row.kind === 'invite'
        ? describeDelivery(row)
        : row.kind === 'link'
          ? describeLinkState(row)
          : row.email && row.email !== row.name
            ? row.email
            : '';
    if (secondary) {
      const sub = document.createElement('div');
      sub.className = 'pb-share-member-email';
      if (row.kind === 'invite' && row.delivery === 'failed') {
        sub.classList.add('is-bad');
      }
      sub.textContent = secondary;
      main.appendChild(sub);
    }

    el.append(avatar, main);

    // THE TRAILING CONTROL IS LABELLED. An owner is a fact and gets static
    // text; everything else is a menu whose trigger states the CURRENT access
    // rather than being a bare caret you have to open to learn anything from.
    const items = itemsFor(row);
    if (row.kind === 'owner' || !items.length) {
      const staticEl = document.createElement('span');
      staticEl.className = 'pb-share-row-static';
      staticEl.textContent = rowAccessLabel(row);
      el.appendChild(staticEl);
      return el;
    }
    el.appendChild(
      buildRowMenu({
        label: rowAccessLabel(row),
        ariaLabel: `${rowAccessLabel(row)} — options for ${row.kind === 'invite' ? row.email : row.name}`,
        items,
        rowId: row.id
      })
    );
    return el;
  }

  /** The word for what this row's access IS, on the control that changes it. */
  function rowAccessLabel(row) {
    if (row.kind === 'owner') return 'Owner';
    if (row.kind === 'invite') return 'Pending';
    if (row.kind === 'link') return 'Can comment';
    return 'Can edit';
  }

  /**
   * The state tag beside the name, or null. Only states that are ABOUT TO
   * MATTER earn one: an invitation nobody has accepted, a link with a week
   * left, a link that is already dead. "Active" is not a tag — it is the
   * absence of one.
   */
  function rowTag(row) {
    if (row.kind === 'invite') {
      return row.live
        ? { tone: 'is-quiet', text: 'Invited' }
        : { tone: 'is-quiet', text: 'Expired' };
    }
    if (row.kind === 'link') {
      if (row.state === 'revoked') return { tone: 'is-danger', text: 'Revoked' };
      if (row.state === 'expired') return { tone: 'is-quiet', text: 'Expired' };
      if (row.expiresInDays != null && row.expiresInDays <= 7) {
        return { tone: 'is-warn', text: `${Math.max(0, row.expiresInDays)}d left` };
      }
    }
    return null;
  }

  function renderRows() {
    const {
      live: liveRows,
      dead,
      counts
    } = mergeAccessRows({
      members,
      invites,
      links,
      selfSub
    });
    list.innerHTML = '';
    for (const row of liveRows) list.appendChild(buildRow(row));

    // The section head carries the count of everything that can reach the
    // project right now — people AND live links, because both are answers to
    // the question the head asks.
    listCount.textContent = `· ${liveRows.length}`;

    // A solo project renders one row — you — and used to leave it at that. Say
    // what this panel is FOR; the owner's invite form is right below, so the
    // empty state points rather than duplicating a control.
    if (counts.people <= 1) {
      const empty = document.createElement('div');
      empty.className = 'pb-share-empty';
      const illo = document.createElement('div');
      illo.className = 'pb-illo pb-illo--sm';
      // Trusted static registry markup — must never route through escapeHTML.
      illo.innerHTML = illustration('team');
      if (illo.firstElementChild) empty.appendChild(illo);
      const emptyTitle = document.createElement('div');
      emptyTitle.className = 'pb-share-empty-title';
      emptyTitle.textContent = "It's just you here";
      const emptyBody = document.createElement('div');
      emptyBody.className = 'pb-share-empty-body';
      emptyBody.textContent =
        selfRole === 'owner'
          ? 'Invite a teammate below and you can edit this project together, live.'
          : 'No one else has access to this project yet.';
      empty.append(emptyTitle, emptyBody);
      list.appendChild(empty);
    }

    // Dead links are history, not access. One disclosure at the end, never
    // interleaved with people who can open the project right now.
    if (dead.length) {
      const fold = document.createElement('div');
      fold.className = 'pb-share-fold';
      const toggle = createButton({
        variant: 'bare',
        className: 'pb-share-fold-toggle',
        label: `${dead.length} expired or revoked link${dead.length > 1 ? 's' : ''}`,
        ariaExpanded: String(foldOpen)
      });
      toggle.addEventListener('click', () => {
        foldOpen = !foldOpen;
        renderRows();
      });
      fold.appendChild(toggle);
      if (foldOpen) for (const row of dead) fold.appendChild(buildRow(row));
      list.appendChild(fold);
    }

    // Re-seat focus onto the equivalent row after the rebuild. Matched by
    // scanning rather than by a selector: a row id is a server-issued sub or
    // invite id, and building a selector out of one needs CSS.escape, which is
    // absent in jsdom — a throw here would take the whole re-render with it.
    if (focusRowId) {
      for (const el of list.children) {
        if (el.dataset?.rowId !== focusRowId) continue;
        el.querySelector('.pb-share-row-trail')?.focus();
        break;
      }
      focusRowId = null;
    }
  }

  /**
   * The one-shot token's own state.
   *
   * The prototype gated the EXIT on having copied. That is cut: `copyTextToClipboard`
   * legitimately returns false on an insecure origin or a denied permission —
   * which is the reason the helper exists — so a copy-gated exit is an
   * inescapable dialog. And it protects nothing: the link is live server-side
   * the instant create returns 201, so dismissing loses the token, not the grant.
   *
   * What replaces it is a guard, not a block: one confirm on the way out while
   * a token is unread, offering the three things a person actually wants.
   *
   * `reveal.items` is a LIST because the compose accepts a list. N reviewers is
   * N links, each shown exactly once — a panel that could hold only one would
   * silently discard the rest, and there is no second chance to read them.
   */
  function renderReveal() {
    actions.textContent = '';
    const panel = document.createElement('div');
    panel.className = 'pb-share-reveal';

    const title = document.createElement('p');
    title.className = 'pb-share-reveal-title';
    title.textContent =
      reveal.items.length > 1
        ? `Here are the ${reveal.items.length} links — this is the only time we can show them`
        : 'Here is the link — this is the only time we can show it';
    panel.appendChild(title);

    const why = document.createElement('p');
    why.className = 'pb-share-reveal-why';
    why.textContent =
      'We store only a fingerprint of it, never the link itself, so nobody — including us — can read it back.';
    panel.appendChild(why);

    reveal.items.forEach((item, index) => {
      const row = document.createElement('div');
      row.className = 'pb-share-reveal-link';
      if (item.copied) row.classList.add('is-copied');

      const main = document.createElement('div');
      main.className = 'pb-share-reveal-link-main';
      const forEl = document.createElement('div');
      forEl.className = 'pb-share-reveal-link-for';
      forEl.textContent = `For ${item.label}`;
      const urlEl = document.createElement('output');
      urlEl.className = 'pb-share-reveal-token';
      urlEl.textContent = item.url;
      main.append(forEl, urlEl);

      const copyBtn = createButton({
        variant: 'primary',
        className: `pb-share-reveal-copy${item.copied ? ' is-copied' : ''}`,
        label: item.copied ? 'Copied' : 'Copy link',
        ariaLabel: `${item.copied ? 'Copied' : 'Copy'} the link for ${item.label}`,
        errorScope: 'ShareModal copy link',
        ...(index === 0 ? { testid: 'share-reveal-copy' } : {}),
        onAsyncClick: async () => {
          await copyLink(index);
        }
      });
      row.append(main, copyBtn);
      panel.appendChild(row);
    });

    const foot = document.createElement('div');
    foot.className = 'pb-share-reveal-actions';
    foot.appendChild(
      createButton({
        variant: 'ghost',
        label: 'Done',
        testid: 'share-reveal-done',
        onClick: () => {
          if (tokenIsUnread()) {
            void guardExit();
            return;
          }
          reveal = null;
          renderActions();
        }
      })
    );
    panel.appendChild(foot);
    actions.appendChild(panel);
  }

  async function copyLink(index = 0) {
    const item = reveal?.items?.[index];
    if (!item) return;
    const ok = await copyTextToClipboard(item.url);
    item.copied = ok === true;
    renderReveal();
    // `ok === false` is a real outcome, not an error: an insecure origin or a
    // denied permission. Say so, and leave the link on screen to select by hand.
    const delivery =
      item.deliveryRequested && item.delivered === true
        ? ' Email sent too.'
        : item.deliveryRequested && item.delivered === false
          ? ' Email was not sent.'
          : '';
    showToast(`${ok ? 'Link copied' : 'Select and copy the link above'}.${delivery}`.trim(), {
      type: ok ? 'success' : 'info'
    });
    announce(
      ok ? 'Review link copied' : 'Copy was blocked — the link is on screen to copy by hand'
    );
  }

  /* ── THE COMPOSE ─────────────────────────────────────────────────────────
   *
   * ONE FIELD. The access type is a property of the invitation — "Can edit" or
   * "Can comment" — and not a separate product behind a separate menu, which is
   * the whole merge expressed as one control. It is built ONCE and painted,
   * never rebuilt: a compose that is torn down and re-created on every `load()`
   * loses the chips a partial failure just left in it, and loses focus with
   * them.
   */

  const ACCESS_MODES = {
    edit: {
      label: 'Can edit',
      placeholder: 'Email address',
      ariaLabel: 'Email addresses',
      inputType: 'email',
      testid: 'share-email-input',
      splitter: /[\s,;]+/,
      validate: isValidRecipient
    },
    comment: {
      label: 'Can comment',
      placeholder: 'Reviewer name, or their email',
      ariaLabel: 'Reviewer names',
      inputType: 'text',
      testid: 'share-reviewer-input',
      // A reviewer name legitimately contains a space ("Dana Whitcomb"); an
      // address never does. So whitespace separates in ONE of these modes.
      splitter: /\s*[,;]+\s*/,
      validate: (value) => {
        const trimmed = String(value || '').trim();
        return trimmed.length > 0 && trimmed.length <= 120;
      }
    },
    // CE-6: the third access type — a ced_ link into the client editor. Field
    // semantics are the comment mode's (a person's name, spaces legal); the
    // difference is the consequence, which the description states.
    contentEdit: {
      label: 'Can edit content',
      placeholder: 'Client name, or their email',
      ariaLabel: 'Client names',
      inputType: 'text',
      testid: 'share-client-input',
      splitter: /\s*[,;]+\s*/,
      validate: (value) => {
        const trimmed = String(value || '').trim();
        return trimmed.length > 0 && trimmed.length <= 120;
      }
    }
  };

  /** The sentence under the compose when the field is empty: what this mode is. */
  function accessDescription(mode) {
    if (mode === 'comment') {
      return 'Opens a read-only copy through a private link and leaves pinned comments. No account needed.';
    }
    if (mode === 'contentEdit') {
      // The boundary the SERVER enforces, stated as the product promise.
      return 'Edits text and images through a private link — layout, style and structure stay locked. No account needed.';
    }
    // DERIVED from what the deployment grants, never asserted — SH-1/D2
    // deleted an unconditional 72-hour promise because it was false in
    // production.
    return capabilities.inviteHint;
  }

  const compose = document.createElement('form');
  compose.className = 'pb-share-compose';

  const recipients = createRecipientField({
    testid: ACCESS_MODES.edit.testid,
    onChange: () => paintCompose()
  });
  compose.appendChild(recipients.el);
  recipients.input.addEventListener('input', () => paintCompose());
  recipients.el.addEventListener('focusin', () => compose.classList.add('is-focused'));
  recipients.el.addEventListener('focusout', () => compose.classList.remove('is-focused'));

  const composeBar = document.createElement('div');
  composeBar.className = 'pb-share-compose-bar';

  const accessBtn = createButton({
    variant: 'secondary',
    className: 'pb-share-access-btn',
    label: ACCESS_MODES.edit.label,
    ariaHaspopup: 'menu',
    testid: 'share-access-btn',
    onClick: () => openAccessMenu()
  });
  const caret = document.createElement('span');
  caret.className = 'pb-share-access-caret';
  caret.setAttribute('aria-hidden', 'true');
  // Trusted static registry markup — never routed through escapeHTML.
  caret.innerHTML = icon('chevronDown', { size: 'sm' }) || '';
  accessBtn.appendChild(caret);

  const composeSpacer = document.createElement('div');
  composeSpacer.className = 'pb-share-compose-spacer';

  const sendBtn = createButton({
    variant: 'primary',
    label: 'Invite',
    type: 'submit',
    testid: 'share-add-btn'
  });
  composeBar.append(accessBtn, composeSpacer, sendBtn);
  compose.appendChild(composeBar);

  /**
   * THE CONSEQUENCE LINE. It says what pressing the button will actually do,
   * and it changes with the mode and with what is in the field, so nobody has
   * to guess which of two products they are about to use.
   */
  const composeSay = document.createElement('p');
  composeSay.className = 'pb-share-compose-say';
  // SH-7: capacity BEFORE the send, not as a 409 afterwards — the server's
  // message names 5 while pending invites are invisible, so an owner with three
  // members and two invites is otherwise told about a number they cannot see.
  //
  // TWO SPANS INSIDE THE ONE LINE, never two siblings after it: a bare span
  // after the `<p>` is an inline box on its own line outside the compose's
  // padding, and renders as a stray sentence hanging off the bottom-left corner
  // of the field. `composeMsg` is the half `paintCompose` rewrites, so the seat
  // fact survives every repaint rather than being wiped by the next one.
  const composeMsg = document.createElement('span');
  composeMsg.className = 'pb-share-compose-msg';
  const seatsEl = document.createElement('span');
  seatsEl.className = 'pb-share-seats';
  composeSay.append(composeMsg, seatsEl);
  compose.appendChild(composeSay);

  compose.addEventListener('submit', (event) => {
    event.preventDefault();
    void submitCompose();
  });

  /** Expiry is a CHOICE, and 30 is the default the API has always applied. */
  let expiresInDays = 30;

  function setAccess(next) {
    if (next === 'comment' && !capabilities.canComment) return;
    if (next === 'contentEdit' && !capabilities.canClientEdit) return;
    rememberAccess(next);
    paintCompose();
  }

  function openAccessMenu() {
    const items = [
      {
        label: ACCESS_MODES.edit.label,
        icon: access === 'edit' ? icon('check', { size: 'sm' }) : undefined,
        onSelect: () => setAccess('edit')
      },
      {
        label: ACCESS_MODES.comment.label,
        icon: access === 'comment' ? icon('check', { size: 'sm' }) : undefined,
        onSelect: () => setAccess('comment')
      }
    ];
    // ABSENT, never disabled: the row exists only where the deployment
    // advertised the capability (rule 2 — undefined means off).
    if (capabilities.canClientEdit) {
      items.push({
        label: ACCESS_MODES.contentEdit.label,
        icon: access === 'contentEdit' ? icon('check', { size: 'sm' }) : undefined,
        onSelect: () => setAccess('contentEdit')
      });
    }
    // Expiry belongs to the link, so it is offered only where a link is what
    // the button makes. The consequence line states the resulting lifetime, so
    // the choice is never hidden from the person making it.
    if (access === 'comment' || access === 'contentEdit') {
      items.push({ type: 'separator' });
      for (const days of [7, 30, 90]) {
        items.push({
          label: `Link lasts ${days} days`,
          icon: expiresInDays === days ? icon('check', { size: 'sm' }) : undefined,
          onSelect: () => {
            expiresInDays = days;
            paintCompose();
          }
        });
      }
    }
    openPopoverMenu(accessBtn, {
      items,
      owner: 'share-modal',
      align: 'left',
      width: 240,
      menuLabel: 'Access type',
      phoneSheet: 'auto'
    });
  }

  function paintCompose() {
    // The capability can go away underneath a remembered preference.
    if (access === 'comment' && !capabilities.canComment) {
      access = 'edit';
      // FN-8: when the user ASKED for a review link by name, silently handing
      // them the invite form is a different operation from the one they chose —
      // view-and-comment became sign-in-and-edit with nothing said. A drifting
      // remembered preference is a different matter and stays quiet.
      if (requestedAccess === 'review' && !announcedReviewFallback) {
        announcedReviewFallback = true;
        setError('Review links are switched off for this project, so this invites people to edit.');
        announce('Review links are unavailable. Switched to inviting people to edit.');
      }
    }
    if (access === 'contentEdit' && !capabilities.canClientEdit) {
      access = capabilities.canComment ? 'comment' : 'edit';
    }
    const mode = ACCESS_MODES[access];

    recipients.setMode(mode);
    recipients.input.dataset.testid = mode.testid;

    const accessLabel = accessBtn.querySelector('.pb-btn-label');
    if (accessLabel) accessLabel.textContent = mode.label;
    // A control that exists to be unavailable is worse than no control: with
    // one access type there is nothing to choose between.
    accessBtn.hidden = !capabilities.canComment && !capabilities.canClientEdit;

    const values = recipients.values();
    const typed = recipients.input.value.trim() ? 1 : 0;
    const pending = values.length + typed;
    const bad = values.filter((value) => !mode.validate(value));

    composeSay.classList.toggle('is-error', bad.length > 0);
    compose.classList.toggle('is-invalid', bad.length > 0);

    if (bad.length) {
      composeMsg.textContent =
        access === 'edit'
          ? `${bad[0]} is not an email address. Editors are invited by email — or switch to “Can comment” and send them a link instead.`
          : `${bad[0]} is too long for a reviewer name.`;
    } else if (!pending) {
      composeMsg.textContent = '';
      const strong = document.createElement('b');
      strong.textContent = mode.label;
      composeMsg.append(strong, document.createTextNode(` — ${accessDescription(access)}`));
    } else if (access === 'edit') {
      const known = values.filter((value) => hasAccessAlready(value));
      composeMsg.textContent = known.length
        ? `${known[0]} already has access — they will not be added twice.`
        : pending === 1
          ? 'They get an email invitation and can edit from their own account.'
          : `${pending} invitations. Each can edit from their own account.`;
    } else if (access === 'contentEdit') {
      composeMsg.textContent =
        pending === 1
          ? `Creates one private editing link, good for ${expiresInDays} days. They can change text and images — nothing else. Shown only once.`
          : `Creates ${pending} private editing links — one per person, each good for ${expiresInDays} days and each shown only once.`;
    } else {
      composeMsg.textContent =
        pending === 1
          ? `Creates one private review link, good for ${expiresInDays} days. We can show it to you only once.`
          : `Creates ${pending} private review links — one per reviewer, each good for ${expiresInDays} days and each shown only once.`;
    }

    // The seat fact rides on the same line, and ONLY in the mode it constrains:
    // a review link consumes no seat, so saying so there would be false.
    if (access === 'edit') {
      const left = seatsRemaining({ members, invites });
      seatsEl.textContent =
        left === 0
          ? ' No seats left — remove someone, or send a review link instead.'
          : pending > left
            ? ` Only ${left} seat${left > 1 ? 's' : ''} left, and ${pending} are queued.`
            : ` ${left} of 5 seats left.`;
    } else {
      seatsEl.textContent = '';
    }

    const label =
      access === 'edit'
        ? pending > 1
          ? `Invite ${pending}`
          : 'Invite'
        : pending > 1
          ? `Create ${pending} links`
          : 'Create link';
    const sendLabel = sendBtn.querySelector('.pb-btn-label');
    if (sendLabel) sendLabel.textContent = label;
    else sendBtn.textContent = label;
    sendBtn.disabled = pending === 0 || bad.length > 0;
  }

  /** Already on the list, under either name — so the compose can say so first. */
  function hasAccessAlready(value) {
    const needle = String(value || '')
      .trim()
      .toLowerCase();
    if (!needle) return false;
    return (
      members.some((m) => String(m.email || '').toLowerCase() === needle) ||
      invites.some((i) => String(i.email || '').toLowerCase() === needle)
    );
  }

  async function submitCompose() {
    recipients.commit();
    const mode = ACCESS_MODES[access];
    const targets = recipients.values();
    if (!targets.length) {
      setError(access === 'edit' ? 'Enter an email address.' : 'Enter a name for the reviewer.');
      recipients.input.focus();
      return;
    }
    const invalid = targets.filter((value) => !mode.validate(value));
    if (invalid.length) {
      setError(
        access === 'edit'
          ? `${invalid[0]} is not an email address.`
          : `${invalid[0]} is too long for a reviewer name.`
      );
      recipients.input.focus();
      return;
    }
    setError('');
    sendBtn.disabled = true;
    recipients.input.disabled = true;
    try {
      if (access === 'comment' || access === 'contentEdit') await createLinks(targets);
      else await addMembers(targets);
    } finally {
      sendBtn.disabled = false;
      recipients.input.disabled = false;
      if (recipients.input.isConnected) recipients.input.focus();
      paintCompose();
    }
  }

  /**
   * N reviewers are N sequential requests, exactly as N addresses are. The same
   * three rules apply, and the third one is sharper here: a token that is not
   * revealed is GONE, so a partial failure must reveal what it created before
   * it reports what it did not.
   */
  async function createLinks(targets) {
    const created = [];
    const failures = [];
    const isContentEdit = access === 'contentEdit';
    for (const reviewerLabel of targets) {
      try {
        const deliverTo =
          capabilities.canDeliverReviewLinks && isValidRecipient(reviewerLabel)
            ? reviewerLabel
            : '';
        const resp = await createReviewLink(projectId, {
          reviewerLabel,
          expiresInDays,
          ...(deliverTo ? { deliverTo } : {}),
          ...(isContentEdit ? { access: 'content-edit' } : {})
        });
        // The token is in THIS response and nowhere else, ever. A content-edit
        // token opens the CLIENT EDITOR entry, never the review page.
        created.push({
          url: isContentEdit
            ? `${window.location.origin}/client.html#ct=${resp.token}`
            : `${window.location.origin}/review.html#rt=${resp.token}`,
          label: reviewerLabel,
          copied: false,
          deliveryRequested: Boolean(deliverTo),
          delivered: resp.delivered
        });
      } catch (err) {
        failures.push({
          label: reviewerLabel,
          message: memberErrorMessage(err, 'Could not create that link.')
        });
        if (!(err instanceof TemplateApiError)) {
          reportError('ShareModal create link', err, { user: 'silent' });
        }
      }
    }

    // The failures stay in the field so they can be fixed in place; the
    // successes are gone from it because they succeeded.
    recipients.keep(failures.map((f) => f.label));

    if (created.length) {
      reveal = { items: created };
      // Reload FIRST, then report — `load()` opens with `setError('')`, so a
      // message written before it is wiped by it.
      await load();
      // Copy on arrival for the single case: pressing the button WAS the intent
      // to get the link. With several, copying one of them silently would be a
      // lie about which one is on the clipboard.
      if (created.length === 1) await copyLink(0);
      else renderActions();
      announce(
        `${created.length} ${isContentEdit ? 'editing' : 'review'} link${created.length === 1 ? '' : 's'} created`
      );
    }

    if (failures.length === 1) setError(failures[0].message);
    else if (failures.length > 1) {
      setError(
        `${failures.length} could not be created: ` +
          failures.map((f) => `${f.label} — ${f.message}`).join('; ')
      );
    }
  }

  async function revokeLink(row) {
    const revoked = await createConfirmDialog({
      title: "Revoke this reviewer's link?",
      message: `${row.name}'s copy stops working immediately and cannot be restored — you would have to send a new link. Their comments stay.`,
      confirmLabel: 'Revoke',
      confirmBusyLabel: 'Revoking…',
      danger: true,
      errorScope: 'ShareModal revoke link',
      onConfirm: async () => {
        const result = await revokeReviewLink(projectId, row.id);
        // SH-1/D3: a mutation that can match zero rows must not report success.
        if (result?.revoked === false) throw new Error('That link was already inactive.');
      }
    });
    if (!revoked) return;
    showToast(`${row.name}'s link revoked`, { type: 'success' });
    announce(`${row.name}'s link revoked`);
    await load();
  }

  async function extendLink(row) {
    await extendReviewLink(projectId, row.id, { expiresInDays: 90 });
    showToast(`${row.name}'s link now expires in 90 days`, { type: 'success' });
    announce(`${row.name}'s link extended`);
    await load();
  }

  function renderActions() {
    actions.textContent = '';
    if (reveal) {
      renderReveal();
      return;
    }
    if (selfRole === 'owner') {
      // The compose is APPENDED, never rebuilt — see its construction above.
      // Re-creating it here is what used to discard the chips a partial failure
      // had just left in it.
      actions.appendChild(compose);
      paintCompose();
      return;
    }
    if (selfRole) {
      actions.appendChild(
        createButton({
          variant: 'danger',
          label: 'Leave project',
          className: 'pb-share-leave',
          testid: 'share-leave-btn',
          busyLabel: null,
          onAsyncClick: () => leaveProject()
        })
      );
    }
  }

  const loader = createDelayedLoader({
    show: () => {
      list.innerHTML = '';
      list.appendChild(renderSkeletonRows(3, { variant: 'revision' }));
    },
    hide: () => {}
  });

  async function load() {
    setError('');
    loader.start?.();
    try {
      const resp = await listProjectMembers(projectId);
      members = Array.isArray(resp?.members) ? resp.members : [];
      selfRole = members.find((m) => m.sub === selfSub)?.role || null;
      // SH-6. Any member may LIST review links — the route has no owner check —
      // and the list call doubles as the capability backstop: it checks the flag
      // BEFORE reading access, so a typed comments_disabled here is the probe.
      links = [];
      linksError = null;
      try {
        const resp2 = await listReviewLinks(projectId);
        links = Array.isArray(resp2?.links) ? resp2.links : [];
      } catch (err) {
        linksError = err instanceof TemplateApiError ? { code: err.data?.code } : { code: null };
        if (!(err instanceof TemplateApiError)) {
          reportError('ShareModal review links', err, { user: 'silent' });
        }
      }
      capabilitiesField = resp?.capabilities || null;
      capabilities = deriveCapabilities({
        capabilitiesField,
        linksError,
        lastAddError
      });

      // SH-9. The one fact that makes a link row worth reading, derived from
      // data already on the wire. FAIL-SOFT and LAST: if this call fails the
      // rows simply carry no count, because a missing nicety must never cost
      // the list it decorates.
      if (links.length) {
        try {
          const feed = await listProjectComments(projectId);
          const counts = countCommentsByLink(feed?.threads || []);
          links = links.map((link) => ({ ...link, comments: counts.get(link.id) ?? 0 }));
        } catch {
          // Expected flow: review comments disabled, or a transient failure.
          // The list is complete without counts; `comments` stays null and the
          // row says nothing rather than saying zero.
        }
      }

      invites = [];
      if (selfRole === 'owner') {
        try {
          const pending = await listProjectInvites(projectId);
          invites = Array.isArray(pending?.invites) ? pending.invites : [];
        } catch (err) {
          // The member list remains useful during a staged rollout where the
          // invite migration/route is not live yet.
          if (!(err instanceof TemplateApiError && [404, 503].includes(err.status))) {
            reportError('ShareModal invites', err, { user: 'silent' });
          }
        }
      }
      loader.stop?.();
      renderRows();
      renderActions();
      renderFooter();
    } catch (err) {
      members = [];
      invites = [];
      links = [];
      selfRole = null;
      actions.textContent = '';
      listCount.textContent = '';
      loader.stop?.();
      renderFooter();
      renderStatus('pb-share-load-error', memberErrorMessage(err, 'Could not load members.'));
      if (!(err instanceof TemplateApiError)) {
        reportError('ShareModal load', err, { user: 'silent' });
      }
    }
  }

  /**
   * SH-7. N recipients are N sequential requests: `POST /members` takes exactly
   * one address, and each one has its own failure code. Three rules follow, and
   * they are the price of accepting a list at all:
   *
   *   · a partial failure NEVER loses the successes;
   *   · the failures stay in the field so they can be fixed in place;
   *   · the report is PER RECIPIENT, not one error line for the whole batch.
   */
  async function addMembers(targets) {
    const added = [];
    const failures = [];
    {
      for (const email of targets) {
        try {
          const resp = await addProjectMember(projectId, email);
          lastAddError = null;
          added.push({ email, resp });
          if (!resp?.existed && !resp?.invited)
            eventBus.emit('collab:membership-changed', { projectId });
        } catch (err) {
          if (err instanceof TemplateApiError) lastAddError = { code: err.data?.code };
          failures.push({
            email,
            code: err instanceof TemplateApiError ? err.data?.code : null,
            message: memberErrorMessage(err, 'Could not add that person.')
          });
          if (!(err instanceof TemplateApiError)) {
            reportError('ShareModal add', err, { user: 'silent' });
          }
        }
      }

      if (added.length === 1) {
        const { email, resp } = added[0];
        const who = resp?.member?.displayName || resp?.member?.email || email;
        showToast(
          resp?.invited
            ? `Invitation sent to ${email}`
            : resp?.existed
              ? `${who} is already a collaborator`
              : `Added ${who}`,
          { type: resp?.existed ? 'info' : 'success' }
        );
      } else if (added.length > 1) {
        showToast(`Added ${added.length} people`, { type: 'success' });
      }
      if (added.length) announce(`${added.length} added`);

      capabilities = deriveCapabilities({ capabilitiesField, linksError, lastAddError });
      // Reload FIRST, then report — `load()` opens with setError(''), so a
      // message written before it is wiped by it. The old single-address form
      // never hit this because it did not reload on failure.
      if (added.length) await load();

      // The failures, named individually, and LEFT IN THE FIELD to be fixed in
      // place. The compose is persistent now, so this is a write to the field
      // itself rather than to a surface variable that survives a rebuild.
      recipients.keep(failures.map((f) => f.email));
      if (failures.length === 1) {
        if (failures[0].code === 'user_not_found' && renderReviewLinkFork(failures[0].email)) {
          setError('');
        } else {
          setError(failures[0].message);
        }
      } else if (failures.length > 1) {
        setError(
          `${failures.length} could not be added: ` +
            failures.map((f) => `${f.email} — ${f.message}`).join('; ')
        );
      }
    }
  }

  async function removeMember(row) {
    setError('');
    const removed = await createConfirmDialog({
      title: 'Remove this collaborator?',
      message: `${row.name} will lose access to this project. You can add them again later.`,
      confirmLabel: 'Remove',
      confirmBusyLabel: 'Removing…',
      danger: true,
      errorScope: 'ShareModal remove',
      onConfirm: async () => {
        try {
          await removeProjectMember(projectId, row.sub);
        } catch (err) {
          throw new Error(memberErrorMessage(err, 'Could not remove that person.'), {
            cause: err
          });
        }
      }
    });
    if (!removed) return;
    showToast(`Removed ${row.name}`, { type: 'success' });
    announce(`${row.name} removed`);
    eventBus.emit('collab:membership-changed', { projectId });
    await load();
  }

  async function revokeInvite(row) {
    const revoked = await createConfirmDialog({
      title: 'Revoke this invitation?',
      message: `${row.email} will no longer be able to use the invitation link.`,
      confirmLabel: 'Revoke',
      confirmBusyLabel: 'Revoking…',
      danger: true,
      errorScope: 'ShareModal revoke invite',
      onConfirm: async () => {
        await revokeProjectInvite(projectId, row.id);
      }
    });
    if (!revoked) return;
    showToast(`Revoked invitation for ${row.email}`, { type: 'success' });
    announce(`Invitation to ${row.email} revoked`);
    await load();
  }

  async function leaveProject() {
    const left = await createConfirmDialog({
      title: 'Leave project?',
      message: projectName
        ? `You'll lose access to “${projectName}” until the owner adds you again.`
        : "You'll lose access to this project until the owner adds you again.",
      confirmLabel: 'Leave',
      confirmBusyLabel: 'Leaving…',
      danger: true,
      errorScope: 'ShareModal leave',
      onConfirm: async () => {
        try {
          await removeProjectMember(projectId, selfSub);
        } catch (err) {
          throw new Error(memberErrorMessage(err, 'Could not leave the project.'), { cause: err });
        }
      }
    });
    if (!left) return;
    showToast('You left the project', { type: 'success' });
    eventBus.emit('collab:membership-changed', { projectId });
    close();
  }

  const ready = load();
  return { overlay, close, ready };
}
